import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from 'lucide-react'
import { useTheme } from 'next-themes'
import type { JSX } from 'react'
import { Toaster as Sonner, type ToasterProps } from 'sonner'

// ─── Toaster — wrapper de Sonner con diseño Emil Kowalski ────────────────────
//
// Principios aplicados:
//   1. resolvedTheme en lugar de theme → Sonner siempre recibe 'light' | 'dark',
//      nunca 'system'. Sin esto, el modo oscuro no se propaga desde el toggle.
//   2. closeButton → el usuario puede descartar manualmente. Los buenos defaults
//      manejan edge cases de forma invisible.
//   3. Durations calibradas: errores duran más (6s) porque requieren acción;
//      confirmaciones son breves (3s) porque solo informan.
//   4. Cohesión de sombra: un toast debe sentirse "sobre" el contenido,
//      no plano. Sombra refinada de dos capas: ambiente + contorno.
//   5. Ancho responsivo: min(356px, 100vw - 2rem) evita que el toast
//      se corte en pantallas pequeñas.

const Toaster = ({ ...props }: ToasterProps): JSX.Element => {
  const { resolvedTheme } = useTheme()

  return (
    <Sonner
      // resolvedTheme siempre resuelve 'system' a 'light' o 'dark'
      theme={(resolvedTheme ?? 'light') as NonNullable<ToasterProps['theme']>}
      position="top-right"
      className="toaster group"
      // Botón X para descarte manual — coherencia con dialog.tsx
      closeButton
      toastOptions={{
        // Durations calibradas por urgencia (Emil: "slow where deliberate, fast where responding")
        duration: 4000,
        classNames: {
          // Las clases de cierre se complementan con los estilos de globals.css
          closeButton: 'sonner-close-btn',
        },
      }}
      icons={{
        success: <CircleCheckIcon className="size-4" />,
        info: <InfoIcon className="size-4" />,
        warning: <TriangleAlertIcon className="size-4" />,
        error: <OctagonXIcon className="size-4" />,
        loading: <Loader2Icon className="size-4 animate-spin" />,
      }}
      style={
        {
          // Tokens del design system de Abakoo
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius)',

          // Ancho responsivo: máx 356px, mín margen de 1rem a cada lado
          '--width': 'min(356px, calc(100vw - 2rem))',

          // Sombra de dos capas: ambiente (largo, difuso) + contorno (corto, nítido)
          // Más pronunciada que los cards pero sin ser pesada
          '--shadow': '0 4px 24px -4px rgba(0, 0, 0, 0.12), 0 2px 8px -2px rgba(0, 0, 0, 0.06)',

          // Espaciado entre toasts apilados — más compacto que el default (14px)
          '--gap': '10px',

          // Tamaño de fuente consistente con el resto del UI
          '--font-size': '0.875rem',
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster }
